import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Globe, Link2, Server, ArrowRight, Info, Loader2, Settings2, ChevronDown } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { getCdnDomain } from '@/lib/utils/cdn';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { validatePathRemapping, extractParamNames, formatParameterNames } from '../../storefront/utils/pathValidation';

interface CreateMountDialogProps {
  id: string;
  onSwitchToMarketplace?: () => void;
}

export function CreateMountDialog({ id, onSwitchToMarketplace }: CreateMountDialogProps) {
  const { t } = useTranslation(['storefront', 'common']);
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [mountType, setMountType] = useState<'alias' | 'customDomain'>('alias');
  const [alias, setAlias] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [basePath, setBasePath] = useState('/');
  const [selectedInstance, setSelectedInstance] = useState<string | null>(null);

  // Path remapping state
  const [enablePathRemap, setEnablePathRemap] = useState(false);
  const [pathRemapFrom, setPathRemapFrom] = useState('');
  const [pathRemapTo, setPathRemapTo] = useState('');
  const [pathValidationError, setPathValidationError] = useState<string | null>(null);

  // Matcher state (optional override)
  const [customMatcher, setCustomMatcher] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Fetch all instances
  const {
    data: instancesData,
    refetch,
    isLoading: isLoadingInstances,
  } = trpc.plugins.listInstances.useQuery(
    { storeId: id },
    {
      enabled: open && !!id,
      refetchOnMount: true, // Always refetch when dialog opens
      refetchOnWindowFocus: false, // Don't refetch on window focus
      staleTime: 0, // Always consider data stale, so it refetches when dialog opens
    },
  );

  // Fetch verified custom domains
  const { data: domainsData } = trpc.plugins.listVerifiedDomains.useQuery(undefined, {
    enabled: open && mountType === 'customDomain',
  });

  // Mount mutation
  const mountMutation = trpc.plugins.mount.useMutation({
    onSuccess: () => {
      utils.plugins.listMountPoints.invalidate();
      setOpen(false);
      setAlias('');
      setCustomDomain('');
      setBasePath('/');
      setSelectedInstance(null);
      setEnablePathRemap(false);
      setPathRemapFrom('');
      setPathRemapTo('');
      setPathValidationError(null);
      setCustomMatcher('');
      setShowAdvancedOptions(false);
      refetch(); // Refresh instances after successful mount
      toast({
        title: t('common:success'),
        description: t('plugins.createMount.toastMountCreated'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common:error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleCreate = async () => {
    if (!selectedInstance) {
      toast({
        title: t('common:error'),
        description: t('plugins.createMount.toastSelectInstance'),
        variant: 'destructive',
      });
      return;
    }

    if (mountType === 'alias' && !alias) {
      toast({
        title: t('common:error'),
        description: t('plugins.createMount.toastAliasRequired'),
        variant: 'destructive',
      });
      return;
    }

    if (mountType === 'customDomain' && !customDomain) {
      toast({
        title: t('common:error'),
        description: t('plugins.createMount.toastCustomDomainRequired'),
        variant: 'destructive',
      });
      return;
    }

    // Find the instance
    const instances = instancesData?.instances || [];
    const instance = instances.find((i: any) => i.id === selectedInstance);
    if (!instance) return;

    try {
      // Determine the matcher to use
      const finalMatcher = customMatcher || instance.metadata?.router?.matcher || '.*';

      const mountParams: any = {
        deploymentId: instance.deploymentId,
        deploymentInstanceId: instance.id,
        storeId: id,
        basePath: basePath || '/',
        matcher: finalMatcher,
        excluder: instance.metadata?.router?.excluder || undefined,
        priority: 0, // First route for a new mount point
      };

      if (mountType === 'alias') {
        mountParams.alias = alias;
      } else if (mountType === 'customDomain') {
        mountParams.customDomain = customDomain;
      }

      // Add path remapping if enabled
      if (enablePathRemap && pathRemapFrom && pathRemapTo) {
        // Validate paths
        if (pathRemapFrom === pathRemapTo) {
          toast({
            title: t('common:error'),
            description: t('plugins.createMount.toastPathsMustDiffer'),
            variant: 'destructive',
          });
          return;
        }

        if (!pathRemapFrom.startsWith('/') || !pathRemapTo.startsWith('/')) {
          toast({
            title: t('common:error'),
            description: t('plugins.createMount.toastPathsMustStartSlash'),
            variant: 'destructive',
          });
          return;
        }

        const validation = validatePathRemapping(pathRemapFrom, pathRemapTo);
        if (!validation.valid) {
          toast({
            title: t('plugins.createMount.toastInvalidPathRemapTitle'),
            description: validation.error || t('plugins.createMount.toastInvalidPathRemapDefault'),
            variant: 'destructive',
          });
          return;
        }

        mountParams.pathRemap = {
          from: pathRemapFrom,
          to: pathRemapTo,
        };
        // Update matcher to match the external path (to)
        mountParams.matcher = pathRemapTo;
      }

      await mountMutation.mutateAsync(mountParams);
    } catch (error) {
      console.error('Create mount failed:', error);
    }
  };

  // Get available remappable paths from selected instance
  const getAvailablePathsForInstance = (instanceId: string | null) => {
    if (!instanceId) return [];

    const instances = instancesData?.instances || [];
    const instance = instances.find((i: any) => i.id === instanceId);
    if (!instance) return [];

    const paths: string[] = [];

    // Get paths from pages (V2 architecture) - ONLY remappable ones
    if (instance.metadata?.pages && Array.isArray(instance.metadata.pages)) {
      instance.metadata.pages.forEach((page: any) => {
        // ✅ Filter: Only include paths explicitly marked as remappable
        if (page.path && page.remappable === true && !paths.includes(page.path)) {
          paths.push(page.path);
        }
      });
    }

    // Note: Legacy entryPoints (V1) don't have remappable flag, so we skip them
    // Path remapping is a V2 feature only

    return paths.sort();
  };

  const availablePaths = getAvailablePathsForInstance(selectedInstance);

  // Filter out preview/forked instances (only show production instances)
  const instances = (instancesData?.instances || []).filter((inst: any) => !inst.isPreview);

  // If no instances available, show message to install plugin first
  const hasNoInstances = instances.length === 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default" size="sm">
          <Plus className="mr-2 h-4 w-4" />
          {t('plugins.createMount.triggerBtn')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('plugins.createMount.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {hasNoInstances
              ? t('plugins.createMount.descriptionInstallFirst')
              : t('plugins.createMount.descriptionMountRoute')}
          </DialogDescription>
        </DialogHeader>

        {isLoadingInstances ? (
          <div className="space-y-4 py-8">
            <div className="text-muted-foreground flex flex-col items-center justify-center gap-4 text-center">
              <Loader2 className="text-primary h-12 w-12 animate-spin" />
              <div>
                <p className="text-sm font-medium">{t('plugins.createMount.loadingInstances')}</p>
                <p className="mt-1 text-xs">{t('plugins.createMount.loadingSubtitle')}</p>
              </div>
            </div>
          </div>
        ) : hasNoInstances ? (
          <div className="space-y-4 py-8">
            <div className="text-muted-foreground flex flex-col items-center justify-center gap-4 text-center">
              <Server className="text-muted-foreground/40 h-16 w-16" />
              <div>
                <p className="text-sm font-medium">{t('plugins.createMount.noInstancesTitle')}</p>
                <p className="mt-1 text-xs">{t('plugins.createMount.noInstancesDesc')}</p>
              </div>
              <Button
                onClick={() => {
                  setOpen(false);
                  onSwitchToMarketplace?.();
                }}
                className="mt-2"
              >
                {t('plugins.createMount.goMarketplaceBtn')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              {/* Mount Type Selection */}
              <Tabs value={mountType} onValueChange={(v) => setMountType(v as any)}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="alias" className="gap-2">
                    <Link2 className="h-4 w-4" />
                    {t('plugins.createMount.aliasTab')}
                  </TabsTrigger>
                  <TabsTrigger value="customDomain" className="gap-2">
                    <Globe className="h-4 w-4" />
                    {t('plugins.createMount.customDomainTab')}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="alias" className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="alias">{t('plugins.createMount.aliasNameLabel')}</Label>
                    <Input
                      id="alias"
                      placeholder={t('plugins.createMount.aliasPlaceholder')}
                      value={alias}
                      onChange={(e) => setAlias(e.target.value)}
                    />
                    <p className="text-muted-foreground text-xs">
                      {t('plugins.createMount.willCreatePrefix')}{' '}
                      <code>
                        {alias || t('plugins.createMount.defaultAliasPlaceholder')}--{id}.cdn.{getCdnDomain()}
                      </code>
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="customDomain" className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="customDomain">{t('plugins.createMount.customDomainLabel')}</Label>
                    {domainsData?.domains && domainsData.domains.length > 0 ? (
                      <>
                        <Select value={customDomain} onValueChange={setCustomDomain}>
                          <SelectTrigger id="customDomain">
                            <SelectValue placeholder={t('plugins.createMount.selectDomainPlaceholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {domainsData.domains.map((domain: any) => (
                              <SelectItem
                                key={domain.id}
                                value={domain.domain}
                                disabled={!domain.isVerified || !domain.enabled}
                              >
                                <div className="flex items-center gap-2">
                                  <span>{domain.domain}</span>
                                  {domain.isVerified && domain.enabled ? (
                                    <Badge
                                      variant="outline"
                                      className="h-4 border-green-200 bg-green-50 text-[10px] text-green-700"
                                    >
                                      {t('plugins.createMount.verifiedBadge')}
                                    </Badge>
                                  ) : !domain.isVerified ? (
                                    <Badge
                                      variant="outline"
                                      className="h-4 border-yellow-200 bg-yellow-50 text-[10px] text-yellow-700"
                                    >
                                      {t('plugins.createMount.notVerifiedBadge')}
                                    </Badge>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="h-4 border-gray-200 bg-gray-50 text-[10px] text-gray-500"
                                    >
                                      {t('plugins.createMount.disabledBadge')}
                                    </Badge>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-muted-foreground text-xs">
                          {t('plugins.createMount.verifiedTotalStats', {
                            verified: domainsData.domains.filter((d: any) => d.isVerified && d.enabled).length,
                            total: domainsData.domains.length,
                          })}
                        </p>
                      </>
                    ) : (
                      <div className="bg-muted/30 rounded-md border border-dashed p-4 text-center">
                        <p className="text-muted-foreground text-sm">{t('plugins.createMount.noVerifiedDomainsMsg')}</p>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {t('plugins.createMount.addVerifyFirstMsg')}
                        </p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Base Path */}
              <div className="space-y-2">
                <Label htmlFor="basePath">{t('plugins.createMount.basePathLabel')}</Label>
                <Input
                  id="basePath"
                  placeholder="/"
                  value={basePath}
                  onChange={(e) => setBasePath(e.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  {t('plugins.createMount.basePathHint')}
                </p>
              </div>

              {/* Instance Selection */}
              <div className="space-y-2">
                <Label>{t('plugins.createMount.routeToLabel')}</Label>
                <div className="bg-muted/30 max-h-[250px] overflow-y-auto rounded-md border p-3">
                  {(() => {
                    // Group instances by plugin → version → config
                    const pluginMap = new Map<string, Map<string, Map<string, any>>>();

                    instances.forEach((inst: any) => {
                      if (!pluginMap.has(inst.pluginId)) {
                        pluginMap.set(inst.pluginId, new Map());
                      }
                      const versionMap = pluginMap.get(inst.pluginId)!;

                      if (!versionMap.has(inst.version)) {
                        versionMap.set(inst.version, new Map());
                      }
                      const configMap = versionMap.get(inst.version)!;

                      const configName = inst.config?.configName || 'default';
                      configMap.set(configName, inst);
                    });

                    return (
                      <Accordion type="multiple" className="w-full">
                        {Array.from(pluginMap.entries()).map(([pluginId, versionMap]) => {
                          // Count total instances for this plugin
                          let instanceCount = 0;
                          versionMap.forEach((configMap) => {
                            instanceCount += configMap.size;
                          });

                          return (
                            <AccordionItem key={pluginId} value={pluginId} className="border-b">
                              <AccordionTrigger className="py-3 hover:no-underline">
                                <div className="flex items-center gap-2 text-left">
                                  <span className="text-sm font-semibold">{pluginId}</span>
                                  <Badge variant="secondary" className="text-xs">
                                    {instanceCount}
                                  </Badge>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="pb-3">
                                <div className="space-y-3 pl-2">
                                  {Array.from(versionMap.entries())
                                    .sort(([a], [b]) => b.localeCompare(a, undefined, { numeric: true }))
                                    .map(([version, configMap]) => (
                                      <div key={version} className="space-y-1.5">
                                        {/* Version Header with count */}
                                        <div className="flex items-center gap-2">
                                          <span className="text-muted-foreground text-xs font-medium">
                                            v{version}
                                          </span>
                                          <span className="text-muted-foreground text-xs">
                                            ({configMap.size} config{configMap.size !== 1 ? 's' : ''})
                                          </span>
                                        </div>

                                        {/* Configs */}
                                        <div className="flex flex-wrap gap-1.5">
                                          {Array.from(configMap.entries())
                                            .sort(([a], [b]) => a.localeCompare(b))
                                            .map(([configName, instance]) => (
                                              <Badge
                                                key={instance.id}
                                                variant={
                                                  selectedInstance === instance.id ? 'default' : 'outline'
                                                }
                                                className={`cursor-pointer transition-all ${
                                                  selectedInstance === instance.id
                                                    ? 'bg-primary'
                                                    : 'hover:bg-primary/20'
                                                }`}
                                                onClick={() => setSelectedInstance(instance.id)}
                                              >
                                                {configName}
                                              </Badge>
                                            ))}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    );
                  })()}
                </div>
              </div>

              {/* Advanced Options - Only show if instance is selected */}
              {selectedInstance && (
                <div className="space-y-3">
                  {/* Path Remapping */}
                  <div className="space-y-3 rounded-lg border p-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="enablePathRemap" className="text-base">
                          {t('plugins.createMount.pathRemappingLabel')}
                        </Label>
                        <p className="text-muted-foreground text-xs">
                          {t('plugins.createMount.pathRemappingHint')}
                        </p>
                      </div>
                      <Switch
                        id="enablePathRemap"
                        checked={enablePathRemap}
                        onCheckedChange={setEnablePathRemap}
                      />
                    </div>

                    {enablePathRemap && (
                      <div className="space-y-3 pt-2">
                        {availablePaths.length === 0 ? (
                          <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
                            <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            <AlertDescription className="space-y-1 text-xs text-amber-900 dark:text-amber-100">
                              <p className="font-medium">{t('plugins.createMount.pathRemapUnavailableTitle')}</p>
                              <p className="text-amber-700 dark:text-amber-300">
                                {t('plugins.createMount.pathRemapUnavailablePrefix')}{' '}
                                <code className="rounded bg-amber-100 px-1 py-0.5 dark:bg-amber-900">
                                  remappable: true
                                </code>{' '}
                                {t('plugins.createMount.pathRemapUnavailableSuffix')}
                              </p>
                            </AlertDescription>
                          </Alert>
                        ) : (
                          <>
                            <div className="space-y-3">
                              {/* From Path */}
                              <div className="space-y-2">
                                <Label htmlFor="pathRemapFrom" className="text-xs font-medium">
                                  {t('plugins.createMount.originalPathLabel')}
                                </Label>
                                <Select
                                  value={pathRemapFrom}
                                  onValueChange={(value) => {
                                    setPathRemapFrom(value);
                                    setPathValidationError(null);
                                    // Auto-suggest a custom path if not already set
                                    if (!pathRemapTo && value) {
                                      // Suggest keeping the same parameter names
                                      const params = extractParamNames(value);
                                      if (params.length > 0) {
                                        // Keep same params, just change the static part
                                        const suggested = value.replace(/^\/[^/:]+/, '/custom');
                                        setPathRemapTo(suggested);
                                      } else {
                                        const suggested = value === '/' ? '/home' : `${value}2`;
                                        setPathRemapTo(suggested);
                                      }
                                    }
                                  }}
                                >
                                  <SelectTrigger id="pathRemapFrom" className="h-auto min-h-[40px] text-sm">
                                    <SelectValue placeholder={t('plugins.createMount.selectPathPlaceholder')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availablePaths.map((path) => {
                                      const hasParams = path.includes(':');
                                      return (
                                        <SelectItem key={path} value={path} className="text-sm">
                                          <div className="flex items-center gap-2">
                                            <span className="font-mono break-all">{path}</span>
                                            {hasParams && (
                                              <Badge variant="secondary" className="h-4 shrink-0 text-[10px]">
                                                {t('plugins.createMount.dynamicBadge')}
                                              </Badge>
                                            )}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                                <p className="text-muted-foreground text-xs">
                                  {t('plugins.createMount.remappablePathsCount', { count: availablePaths.length })}
                                </p>
                              </div>

                              {/* Arrow Divider */}
                              {pathRemapFrom && (
                                <div className="flex justify-center">
                                  <ArrowRight className="text-muted-foreground h-5 w-5" />
                                </div>
                              )}

                              {/* To Path */}
                              <div className="space-y-2">
                                <Label htmlFor="pathRemapTo" className="text-xs font-medium">
                                  {t('plugins.createMount.customPathLabel')}
                                </Label>
                                <Input
                                  id="pathRemapTo"
                                  placeholder={t('plugins.createMount.customPathPlaceholder')}
                                  value={pathRemapTo}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    setPathRemapTo(newValue);
                                    setPathValidationError(null);

                                    if (pathRemapFrom && newValue) {
                                      const validation = validatePathRemapping(pathRemapFrom, newValue);
                                      if (!validation.valid) {
                                        setPathValidationError(validation.error || t('plugins.createMount.invalidPathErrorTitle'));
                                      }
                                    }
                                  }}
                                  className={`text-sm font-mono ${pathValidationError ? 'border-red-500' : ''}`}
                                  disabled={!pathRemapFrom}
                                />
                                <p className="text-muted-foreground text-xs">{t('plugins.createMount.customPathHint')}</p>
                              </div>

                              {/* Parameter Requirements */}
                              {pathRemapFrom && extractParamNames(pathRemapFrom).length > 0 && (
                                <div className="bg-blue-50 dark:bg-blue-950 rounded-md border border-blue-200 dark:border-blue-800 p-2.5 text-xs">
                                  <div className="flex items-start gap-2">
                                    <Info className="text-blue-600 dark:text-blue-400 mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    <div className="space-y-1">
                                      <p className="text-blue-900 dark:text-blue-100 font-medium">
                                        {t('plugins.createMount.requiredParamsLabel')} <code className="text-blue-700 dark:text-blue-300">{formatParameterNames(extractParamNames(pathRemapFrom))}</code>
                                      </p>
                                      <p className="text-blue-700 dark:text-blue-300">
                                        {t('plugins.createMount.paramOrderHint')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Validation Error */}
                              {pathValidationError && (
                                <div className="bg-red-50 dark:bg-red-950 rounded-md border border-red-200 dark:border-red-800 p-2.5 text-xs">
                                  <div className="flex items-start gap-2">
                                    <span className="text-red-600 dark:text-red-400">❌</span>
                                    <div className="space-y-1">
                                      <p className="text-red-900 dark:text-red-100 font-medium">{t('plugins.createMount.invalidPathErrorTitle')}</p>
                                      <p className="text-red-700 dark:text-red-300">{pathValidationError}</p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Success Result */}
                              {!pathValidationError && pathRemapFrom && pathRemapTo && pathRemapFrom !== pathRemapTo && (
                                <div className="bg-green-50 dark:bg-green-950 space-y-2 rounded-md border border-green-200 dark:border-green-800 p-2.5 text-xs">
                                  <div className="flex items-start gap-2">
                                    <span className="text-green-600 dark:text-green-400 font-medium">✓</span>
                                    <div className="space-y-1 min-w-0 flex-1">
                                      <p className="text-green-900 dark:text-green-100">
                                        <span className="font-medium">{t('plugins.createMount.resultPrefix')}</span> {t('plugins.createMount.resultUsersVisit')}{' '}
                                        <code className="rounded bg-green-100 dark:bg-green-900 px-1 py-0.5 font-mono text-green-700 dark:text-green-300 break-all">
                                          {pathRemapTo}
                                        </code>
                                        {t('plugins.createMount.resultPluginReceives')}{' '}
                                        <code className="rounded bg-blue-100 dark:bg-blue-900 px-1 py-0.5 font-mono text-blue-700 dark:text-blue-300 break-all">
                                          {pathRemapFrom}
                                        </code>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Advanced Matcher Options */}
                  <div className="rounded-lg border p-4">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      className="w-full justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Settings2 className="h-4 w-4" />
                        <span>{t('plugins.createMount.advancedMatcherBtn')}</span>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${showAdvancedOptions ? 'rotate-180' : ''}`}
                      />
                    </Button>

                    {showAdvancedOptions && (
                      <div className="mt-4 space-y-3 border-t pt-3">
                        <div className="space-y-2">
                          <Label htmlFor="customMatcher" className="text-sm">
                            {t('plugins.createMount.matcherPatternLabel')}
                          </Label>
                          <Input
                            id="customMatcher"
                            placeholder={t('plugins.createMount.matcherPatternPlaceholder')}
                            value={customMatcher}
                            onChange={(e) => setCustomMatcher(e.target.value)}
                            className="font-mono text-sm"
                          />
                          <p className="text-muted-foreground text-xs">
                            {t('plugins.createMount.defaultLabel')}{' '}
                            <code className="bg-muted rounded px-1 py-0.5">
                              {(() => {
                                const instances = instancesData?.instances || [];
                                const instance = instances.find((i: any) => i.id === selectedInstance);
                                return instance?.metadata?.router?.matcher || '.*';
                              })()}
                            </code>
                          </p>
                        </div>

                        <div className="bg-muted/50 space-y-2 rounded-md p-3 text-xs">
                          <p className="font-medium">{t('plugins.createMount.patternExamplesTitle')}</p>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <code className="bg-background rounded px-1.5 py-0.5">.*</code>
                              <span className="text-muted-foreground">{t('plugins.createMount.patternAllDesc')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <code className="bg-background rounded px-1.5 py-0.5">/checkout</code>
                              <span className="text-muted-foreground">{t('plugins.createMount.patternCheckoutDesc')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <code className="bg-background rounded px-1.5 py-0.5">
                                /hello-with-param/:id
                              </code>
                              <span className="text-muted-foreground">{t('plugins.createMount.patternParamDesc')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <code className="bg-background rounded px-1.5 py-0.5">/{`*`}splat</code>
                              <span className="text-muted-foreground">{t('plugins.createMount.patternWildcardDesc')}</span>
                            </div>
                          </div>
                          <p className="text-muted-foreground mt-2">
                            {t('plugins.createMount.patternSyntaxIntro')} <strong>path-to-regexp</strong> {t('plugins.createMount.patternSyntaxSuffix')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                {t('common:cancel')}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={
                  !selectedInstance ||
                  (mountType === 'alias' && !alias) ||
                  (mountType === 'customDomain' && !customDomain) ||
                  (enablePathRemap && !!pathValidationError) ||
                  mountMutation.isPending
                }
              >
                {mountMutation.isPending ? t('plugins.createMount.creatingBtn') : t('plugins.createMount.createBtn')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
